import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";

const COOKIE_CONSENT_KEY = "cookie-consent";

export type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const defaultPreferences: CookiePreferences = {
  necessary: true, // Always required
  analytics: false,
  marketing: false,
};

export const getCookiePreferences = (): CookiePreferences | null => {
  const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    // Check if user has already made a choice
    const existingConsent = getCookiePreferences();
    if (!existingConsent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectNonEssential = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 sm:left-auto sm:right-4 sm:bottom-4">
      <div className="mx-auto sm:mx-0 max-w-md">
        <div className="rounded-lg border border-border bg-background shadow-lg">
          <div className="p-3 sm:p-4">
            <div className="flex items-start gap-3">
              <div className="hidden sm:block">
                <div className="rounded-full bg-primary/10 p-1.5">
                  <Cookie className="h-4 w-4 text-primary" />
                </div>
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold">Cookie Preferences</h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    We use cookies to enhance your experience.{" "}
                     <Link to="/privacy-policy" className="text-primary underline hover:no-underline">
                       Learn more about our cookie policy
                     </Link>
                  </p>
                </div>

                {showDetails && (
                  <div className="space-y-3 rounded-md bg-muted/50 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Necessary Cookies</p>
                        <p className="text-xs text-muted-foreground">
                          Required for the website to function properly
                        </p>
                      </div>
                      <div className="text-xs text-muted-foreground">Always active</div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Analytics Cookies</p>
                        <p className="text-xs text-muted-foreground">
                          Help us understand how visitors use our website
                        </p>
                      </div>
                      <label className="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          checked={preferences.analytics}
                          onChange={(e) =>
                            setPreferences({ ...preferences, analytics: e.target.checked })
                          }
                          className="peer sr-only"
                        />
                        <div className="h-5 w-9 rounded-full bg-muted peer-checked:bg-primary after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-background after:transition-all peer-checked:after:translate-x-full" />
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Marketing Cookies</p>
                        <p className="text-xs text-muted-foreground">
                          Used to deliver personalised advertisements
                        </p>
                      </div>
                      <label className="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          checked={preferences.marketing}
                          onChange={(e) =>
                            setPreferences({ ...preferences, marketing: e.target.checked })
                          }
                          className="peer sr-only"
                        />
                        <div className="h-5 w-9 rounded-full bg-muted peer-checked:bg-primary after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-background after:transition-all peer-checked:after:translate-x-full" />
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2">
                  {showDetails ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDetails(false)}
                        className="flex-1 sm:flex-none"
                      >
                        Back
                      </Button>
                      <Button
                        variant="hero"
                        size="sm"
                        onClick={handleSavePreferences}
                        className="flex-1 sm:flex-none"
                      >
                        Save Preferences
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDetails(true)}
                        className="flex-1 sm:flex-none"
                      >
                        Customise
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRejectNonEssential}
                        className="flex-1 sm:flex-none"
                      >
                        Reject Non-Essential
                      </Button>
                      <Button
                        variant="hero"
                        size="sm"
                        onClick={handleAcceptAll}
                        className="flex-1 sm:flex-none"
                      >
                        Accept All
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
